import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { SidePanelService } from '../../layout/side-panel/side-panel.service';
import { PreferencesService } from '../../preferences/preferences-service/preferences.service';
import { SyncService } from '../../sync/sync.service';

import { TUISettings } from '../../ui/ui.model';

import { Title } from '@angular/platform-browser';
import { TPointResponse } from '@project/view-models';
import { TranslationPipe } from '../../../features/translate/translation.pipe';
import { EStore } from '../../../shared/enums/store.enum';
import { TSidePanel } from '../../layout/side-panel/side-panel.model';
import { PointsFetchingService } from '../../points/points-fetching.service';
import { TUser } from '../../user/user.model';
import { ReminderSearchService } from '../reminder-search.service';
import { RemindersService } from '../reminders.service';

@Component({
  selector: 'pp-reminders',
  templateUrl: './reminders.component.html',
  styleUrls: ['./reminders.component.scss'],
  standalone: false,
})
export class RemindersComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  sidePanel: TSidePanel = this.sidePanelService.getSidePanel();
  filterDataFetched = false;

  ui$: Observable<TUISettings>;
  ui: TUISettings;
  user$: Observable<TUser>;
  user: TUser;
  routerPath = this.router.url;

  constructor(
    private store: Store<{
      ui: TUISettings;
      user: TUser;
    }>,
    private sidePanelService: SidePanelService,
    private syncService: SyncService,
    private preferencesService: PreferencesService,
    private translationPipe: TranslationPipe,
    private router: Router,
    private titleService: Title,
    private pointsFetchingService: PointsFetchingService,
    private remindersService: RemindersService,
    private reminderSearchService: ReminderSearchService,
  ) {
    this.ui$ = this.store.pipe(select(EStore.UI));
    this.user$ = this.store.pipe(select(EStore.USER));

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.routerPath = event.url;
      }
    });
  }

  ngOnInit() {
    this.titleService.setTitle(this.translationPipe.transform('reminders') + ' | Pinpoint Works');

    this.sidePanelService.initSidePanel({ expandedInitially: false });

    this.ui$.pipe(takeUntil(this.destroy$)).subscribe((ui) => {
      this.ui = ui;
    });

    this.firstLoad();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private firstLoad(): void {
    this.syncService
      .firstLoad()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.preferencesService.fetchPreferences()),
        switchMap(() => this.fetchReminderPoints()),
        tap(() => {
          this.reminderSearchService.searchReminders().subscribe();
          this.filterDataFetched = true;
        }),
      )
      .subscribe();
  }

  private fetchReminderPoints(): Observable<TPointResponse[]> {
    const reminders = this.remindersService.getReminders();

    const reminderPoints = reminders
      .map((reminder) => reminder.pointId)
      .filter((pointId) => !!pointId);

    return this.pointsFetchingService.fetchPointsByIds(reminderPoints);
  }
}
